import { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { FreightRecord, getFreightRecord } from '../api/handleFreightRecords';
import FreightForm from '../components/FreightForm';
import { supabase } from '../supabaseClient';
import { useAlert } from '../contexts/AlertContext';
import { useAccount } from '../contexts/AccountContext';

const FreightRecordReview = () => {
    const { recordId } = useParams<{ recordId: string }>();
    const location = useLocation();
    const { showAlert } = useAlert();
    const { currentAccount, isImpersonating, impersonatedUser } = useAccount();
    const [record, setRecord] = useState<FreightRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchRecord = async () => {
            try {
                setLoading(true);
                if (!recordId) {
                    throw new Error('No record ID provided');
                }

                const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                    isImpersonating: true,
                    impersonatedUserId: impersonatedUser.id,
                    accountId: currentAccount.account_id,
                } : undefined;

                const data = await getFreightRecord(recordId, impersonationData);
                setRecord(data);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to load record';
                setError(errorMessage);
                showAlert(errorMessage, 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchRecord();
    }, [recordId, showAlert, currentAccount, isImpersonating, impersonatedUser]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error || !record) {
        return (
            <Box p={2}>
                <Typography color="error" align="center">
                    {error || 'Record not found'}
                </Typography>
            </Box>
        );
    }

    return <FreightRecordReviewDetails record={record} unmatchedCompanies={location.state?.unmatchedCompanies} />;
};

type FreightRecordReviewProps = {
    record: FreightRecord;
    unmatchedCompanies?: Array<{
        extractedName: string;
        role: 'carrier' | 'supplier' | 'outlet';
        field: string;
        suggestions?: Array<{ id: string; name: string; types: string[] }>;
    }>;
};

const FreightRecordReviewDetails = ({ record, unmatchedCompanies }: FreightRecordReviewProps) => {
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [fileType, setFileType] = useState<'pdf' | 'image' | 'other' | null>(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { showAlert } = useAlert();

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchUrl = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!record.attachmentUrls || !record.attachmentUrls[0]) {
                    setLoading(false);
                    return;
                }

                const filePath = record.attachmentUrls[0];

                // Get public URL for the file
                const { data } = supabase.storage
                    .from('documents')
                    .getPublicUrl(filePath);

                if (!data.publicUrl) {
                    throw new Error('Failed to get public URL for file');
                }

                setFileUrl(data.publicUrl);

                const extension = filePath.split('.').pop()?.toLowerCase();
                if (extension === 'pdf') setFileType('pdf');
                else if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension!)) setFileType('image');
                else setFileType('other');

            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to load file';
                console.error('Failed to fetch file:', error);
                setError(errorMessage);
                showAlert(errorMessage, 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchUrl();
    }, [record.attachmentUrls, showAlert]);

    const renderDetails = (
        <Box p={2}>
            <Typography variant="h6" gutterBottom>Record Details</Typography>
            <FreightForm initialData={record} unmatchedCompanies={unmatchedCompanies} />
        </Box>
    );

    const renderPreview = (
        <Box
            p={2}
            sx={{
                height: '100%',         // or '100vh' if you want full screen
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error" align="center">
                    {error}
                </Typography>
            ) : !fileUrl || !fileType ? (
                <Typography align="center">No file available</Typography>
            ) : fileType === 'pdf' ? (
                <object
                    data={fileUrl + '#view=FitH'}
                    type="application/pdf"
                    style={{
                        flex: 1,
                        width: '100%',
                    }}
                >
                    <p>
                        Your browser does not support PDFs.
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                            Download the PDF
                        </a>
                    </p>
                </object>
            ) : fileType === 'image' ? (
                <Box component="img" src={fileUrl} alt="Uploaded" maxWidth="100%" maxHeight="100%" />
            ) : (
                <Typography>
                    File type not supported for inline preview.{' '}
                    <a href={fileUrl} target="_blank" rel="noopener noreferrer">Download</a>
                </Typography>
            )}
        </Box>
    );

    if (isSmallScreen) {
        // 📱 Small screen: use Tabs
        return (
            <Box>
                <Tabs value={tabIndex} onChange={(_, newIndex) => setTabIndex(newIndex)} centered>
                    <Tab label="Record" />
                    <Tab label="File" />
                </Tabs>
                {tabIndex === 0 ? renderDetails : renderPreview}
            </Box>
        );
    }

    // 💻 Larger screen: side-by-side layout
    return (
        <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: '1fr 2fr' }}
            gap={2}
        >
            <Box
                border={1}
                borderColor="grey.300"
                borderRadius={2}
                p={2}
            >
                {renderDetails}
            </Box>

            <Box>
                {renderPreview}
            </Box>
        </Box>

    );
};

export default FreightRecordReview;
